import React, { useCallback, useState, useEffect } from "react";
import { Form, Modal } from "@components";
import { Box, Button, Grid } from "@material-ui/core";
import { Manager } from "@components/form/state/types";
import {
  AccessAdminForProp,
  AccessAdminGroupRole,
} from "@api/types/accessadmin";
import api from "@api";
import { FilterType, Table } from "@components";
import { Field } from "../../../../../../components/form/fields/field";
import { Cell } from "@components/table/types";
import { getGroupRolesByUserGroupId } from "@api/admin/accessadmin";
import DeleteIcon from "@material-ui/icons/Delete";
import { alertService } from "@services";
import { ConfirmationModal } from "../../../../../request/steps/summary/views/summary/confirmation-modal";

const headers: Cell[] = [{ data: "Member" }, { data: "Action" }];

interface Props {
  open: boolean;
  onCancel: () => void;
  form: Manager<AccessAdminForProp>;
}

type Key = keyof AccessAdminForProp;

export const AccessAdminDetail: React.FC<Props> = ({
  open,
  onCancel,
  form,
}) => {
  let groupRole: AccessAdminGroupRole = {
    id: 0,
    emailAddress: "",
    userGroupId: 0,
  };

  function clearData(isClose: boolean) {
    groupRole.emailAddress = "";
    form.values.emailAddress = "";
    if (isClose) {
      groupRole.id = 0;
      form.reset();
      setMemberEmails([]);
      onCancel();
    }
  }

  const [isSubmitting, setSubmitting] = useState(false);
  const [memberEmails, setMemberEmails] = useState([]);
  const [isFetchingGroupRole, setIsFetchingGroupRole] = useState(false);
  const [isConfirming, setConfirmingState] = useState<boolean>(false);
  const [idToBeDeleted, setIdToBeDeleted] = useState<any>([]);
  const [modalMessage, setModalMessage] = useState<string>("");

  useEffect(() => {
    if (form.values.id) {
      getGroupRolesByUserGroupId();
    }
  }, [form.values.id]);

  // clear
  useEffect(() => {
    clearData(false);
  }, []);

  function getGroupRolesByUserGroupId() {
    setIsFetchingGroupRole(true);
    api.accessadmin
      .getGroupRolesByUserGroupId(form.values.id)
      .then((response) => {
        setMemberEmails(response.accessAdminGroupRoles);
      })
      .catch((error) => {
        console.log(error);
      })
      .then(() => {
        setIsFetchingGroupRole(false);
      });
  }

  const handleSubmit = useCallback(() => {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          submitData();
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }, [form]);

  function submitData() {
    if (form.values.id) {
      groupRole.id = 0; // 0 for default primary key
      groupRole.emailAddress = form.values.emailAddress;
      groupRole.userGroupId = form.values.id;
      setSubmitting(true);

      console.log(groupRole);
      // CREATE
      api.accessadmin
        .postGroupRole(groupRole)
        .then((response) => {
          alertService.success({
            msg: "Email has been successfully added to this user group.",
          });
          getGroupRolesByUserGroupId();
          clearData(false);
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({ statusCode: error.response.status });
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "emailAddress":
        return Promise.resolve([
          name as Key,
          Form.Validators.hasValue(value) ||
            Form.Validators.isEmail(value as string),
        ]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function onChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  function handleConfirmationCancel() {
    closeConfirmationModal();
  }

  function handleConfirmationConfirm() {
    setConfirmingState(true);
    deleteGroupRoleById(idToBeDeleted);
  }

  function openConfirmationModal(data: any) {
    setModalMessage("Are you sure do you want to remove this data?");
    setIdToBeDeleted(data);
    setConfirmingState(true);
  }

  function closeConfirmationModal() {
    setModalMessage("");
    setIdToBeDeleted([]);
    setConfirmingState(false);
  }

  function deleteGroupRoleById(data: any) {
    if (data.id) {
      api.accessadmin
        .deleteGroupRoleById(data.id)
        .then((response) => {
          if (response) {
            getGroupRolesByUserGroupId();
            alertService.success({
              msg: "Group role has been successfully deleted",
            });
            clearData(false);
          } else {
            alertService.danger({
              msg: "Something went wrong. Failed to delete.",
            });
          }
          closeConfirmationModal();
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({ statusCode: error.response.status });
            closeConfirmationModal();
          }
        });
    }
  }

  const modalTitle =
    form.values.id !== 0 ? form.values.groupName : "None selected";
  const submitText = isSubmitting ? "Adding..." : "Add";

  const deleteIcon = (
    <span
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <DeleteIcon />
    </span>
  );

  return (
    <Modal
      isOpen={open}
      title={`Admin Member / ${modalTitle}`}
      closeModal={() => clearData(true)}
      size="sm"
    >
      <Form>
        <Form.Textbox
          label="Email Address"
          name="emailAddress"
          onChange={onChange}
          size={6}
          error={form.errors.emailAddress}
          value={form.values.emailAddress}
          placeholder="Enter Email Address"
          required
        />
      </Form>
      <Form>
        <Field label="" size={1}>
          <Button
            type="button"
            color="primary"
            disabled={isSubmitting}
            onClick={handleSubmit}
          >
            {submitText}
          </Button>
        </Field>
        <Grid container item xs={12}>
          <Table
            data={memberEmails.map((data: any) => ({
              cells: [
                {
                  data: data.email,
                  color: "blue",
                  cursor: "pointer",
                  onClick: () => {},
                },
                {
                  data: deleteIcon,
                  color: "gray",
                  cursor: "pointer",
                  onClick: () => {
                    openConfirmationModal(data);
                  },
                },
              ],
              data: data,
            }))}
            tableName={"Group Members"}
            headers={headers}
            filters={[FilterType.SORT, FilterType.SEARCH, FilterType.PAGINATOR]}
            loading={isFetchingGroupRole}
            searchColumns={["Member"]}
            toolbar="M_ACCESS_ADMIN_MODAL_TOOLBAR"
          />
        </Grid>
      </Form>
      <Box textAlign="right" mt={4}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => clearData(true)}
        >
          Close
        </Button>
      </Box>
      <ConfirmationModal
        onCancel={handleConfirmationCancel}
        onConfirm={handleConfirmationConfirm}
        open={isConfirming}
        customMessage={modalMessage}
      />
    </Modal>
  );
};
